import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useGoogleLogin } from "@react-oauth/google";
import { useLocation, useSearchParams } from "react-router-dom";
import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, AuthContext } from "context/AuthContext";
import { StateSignin, login } from "lib/actions";
import { SvgLogo, SvgGoogle, SvgMicrosoft } from "./svgs";
import { openSSOWindow } from "lib/utils";
import { showToast } from "./components/common";
import { Divider, Form, Input, Button } from "antd";
import { SSOType, SSOPage, SSOPostData } from "pages/dashboard/sso/page";

export default function FormLogin({ isAplusguard }) {
  const { setAuthenticated, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  interface formDataType {
    [key: string]: FormDataEntryValue;
  }
  const responseBody: formDataType = {};
  const [responseData, setResponseData] = useState<StateSignin>();
  const [btnSubmitDisabled, setBtnSubmitDisabled] = useState(false);
  const microsoftWindow = useRef<Window | null>(null);
  const googleWindow = useRef<Window | null>(null);

  useEffect(() => {
    const ssoCallBack = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      console.log("Received message:", event.data);
      // @ts-ignore
      const keepSsoWindow = window.keepSsoWindow;
      try {
        const data = JSON.parse(event.data) as SSOPostData;
        if (data?.ssoType === SSOType.MicrosoftSSO) {
          !keepSsoWindow && microsoftWindow.current?.close();
        } else if (data?.ssoType === SSOType.GoogleSSO) {
          !keepSsoWindow && googleWindow.current?.close();
        } else {
          return;
        }
        if (data.success) {
          setAuthenticated(true);
          setUser(data.data);
          navigate("/dashboard");
        } else {
          setResponseData({ message: data.error?.errorString });
        }

        setBtnSubmitDisabled(false);
      } catch (error) {
        console.warn("Failed to parse data", error);
        return;
      }
    };
    window.addEventListener("message", ssoCallBack);
    return () => {
      window.removeEventListener("message", ssoCallBack);
    };
  }, []);

  const onFinish = (values) => {
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      formData.append(key, values[key]);
    });
    handleSubmit(formData);
  };

  async function handleSubmit(formData) {
    formData.forEach(
      (value, property: string) => (responseBody[property] = value),
    );
    try {
      setBtnSubmitDisabled(true);
      setResponseData({});
      const response = await login(formData);
      //setResponseData(response);
      const user: User = {
        id: response.user.id,
        email: response.user.email,
        accountName: response.user.organization.name,
        orgId: response.user.organization.id,
        role: response.user.role.name,
        token: response.token,
        phoneNumber: response.user.phoneNumber,
        name: response.user.firstName + " " + response.user.lastName,
        organization: response.user.organization,
      };
      setAuthenticated(true);
      setUser(user);
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      setResponseData({ message: "The user name or password is incorrect" });
    } finally {
      setBtnSubmitDisabled(false);
    }
  }

  return (
    <Form name="login" onFinish={onFinish} className=" space-y-3 ">
      <div className="flex-1 rounded-3xl shadow-lg border bg-gray-50 px-6 pb-4 pt-8">
        <div className="text-center flex items-center justify-center gap-1">
          {(() => {
            if (isAplusguard)
              return <SvgLogo className="fill-neutral-800" width="50" />;
            else
              return (
                <img
                  src="./alpha_vision_logo.png"
                  className="w-[150px] md:w-[220px] "
                />
              );
          })()}
        </div>
        <div className="w-full">
          <div>
            <div className="relative mb-3 mt-8">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "Please enter email!" },
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
                className="mb-0"
              >
                <Input
                  placeholder="Email"
                  className="peer block w-full rounded-2xl border border-gray-200 py-[16px] pl-3 text-sm outline-2 placeholder:text-gray-400 placeholder:text-base"
                />
              </Form.Item>
            </div>
          </div>
          <div className="mt-5">
            <div className="relative">
              <Form.Item
                name="password"
                rules={[{ required: true, message: "Please enter password!" }]}
                className="mb-0"
              >
                <Input.Password
                  placeholder="Password"
                  className="w-full rounded-2xl border border-gray-200 py-[16px] pl-3 text-sm outline-2 placeholder:text-gray-400 placeholder:text-base"
                />
              </Form.Item>
              {/* <KeyIcon className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-500 peer-focus:text-gray-900" /> */}
            </div>
          </div>
        </div>
        <div className="w-full flex justify-center mt-5">
          {/* <button
            className="w-[160px] flex justify-center h-[54px] rounded-full items-center bg-blue-500 px-4 text-base font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            type="submit"
            aria-disabled={btnSubmitDisabled}
          >
            Log in
          </button> */}
          <Button
            className="w-[160px] flex justify-center h-[54px] rounded-full items-center bg-blue-500 px-4 text-base font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            type="primary"
            htmlType="submit"
            disabled={btnSubmitDisabled}
          >
            Log in
          </Button>
        </div>

        {responseData?.message && (
          <div
            className="flex items-end space-x-1"
            aria-live="polite"
            aria-atomic="true"
          >
            <>
              <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-500">{responseData.message}</p>
            </>
          </div>
        )}
        <div
          className="h-8 "
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#3E83F6",
            alignContent: "end",
            textAlign: "center",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/reset-password")}
          >
            Forget Password
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-[163px] h-full">
            <Divider className="" />
          </div>
          or
          <div className="w-[163px] h-full">
            <Divider />
          </div>
        </div>
        <div className="w-full flex justify-center items-center flex-col gap-4">
          <button
            className="w-[241px] h-[44px] flex items-center justify-center gap-2 border border-[#BDBDBD] rounded-2xl text-sm font-semibold hover:text-zinc-500"
            onClick={(e) => {
              e.preventDefault();
              //loginGoogle();
              googleWindow.current = openSSOWindow(
                SSOPage.Login,
                SSOType.GoogleSSO,
              );
            }}
          >
            <SvgGoogle width="21" />
            Log in With Google
          </button>

          <button
            className="w-[241px] h-[44px] flex items-center justify-center gap-2 border border-[#BDBDBD] rounded-2xl text-sm font-semibold hover:text-zinc-500"
            onClick={(e) => {
              e.preventDefault();
              microsoftWindow.current = openSSOWindow(
                SSOPage.Login,
                SSOType.MicrosoftSSO,
              );
            }}
          >
            <SvgMicrosoft width="21" />
            Log in With Microsoft
          </button>
        </div>
      </div>
    </Form>
  );
}
